/*
 * @author  Agence Dn'D <contact@dnd.fr>
 * @license https://opensource.org/licenses/osl-3.0.php  Open Software License (OSL 3.0)
 * @link    https://www.dnd.fr/
 */

class ContactModal {
    constructor(modalElement) {
        this.modal = modalElement;
        this.trigger = modalElement.querySelector('[data-contact-trigger]');
        this.container = modalElement.querySelector('[data-contact-container]');
        this.closeBtn = modalElement.querySelector('[data-contact-close]');
        this.isOpen = false;
        this.focusableElements = null;
        this.previousFocus = null;

        this.init();
    }

    init() {
        if (!this.trigger || !this.container) return;

        // Opening/closing events
        this.trigger.addEventListener('click', () => this.toggle());
        this.closeBtn?.addEventListener('click', () => this.close());

        // Click closure on the outside
        document.addEventListener('click', (e) => {
            if (this.isOpen && !this.container.contains(e.target) && !this.trigger.contains(e.target)) {
                this.close();
            }
        });

        // Close with the Esc key
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.isOpen) {
                this.close();
            }
        });

        // Focus trap management
        this.container.addEventListener('keydown', (e) => {
            if (e.key === 'Tab' && this.isOpen) {
                this.handleTabKey(e);
            }
        });
    }

    toggle() {
        this.isOpen ? this.close() : this.open();
    }

    open() {
        this.isOpen = true;
        this.previousFocus = document.activeElement;

        // Display
        this.container.hidden = false;
        this.trigger.setAttribute('aria-expanded', 'true');

        // Focus on the close button
        requestAnimationFrame(() => {
            this.closeBtn?.focus();
            this.updateFocusableElements();
        });
    }

    close() {
        this.isOpen = false;

        // Masking
        this.container.hidden = true;
        this.trigger.setAttribute('aria-expanded', 'false');

        // Restoring focus
        if (this.previousFocus) {
            this.previousFocus.focus();
            this.previousFocus = null;
        }
    }

    updateFocusableElements() {
        const focusableSelectors =
            'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

        this.focusableElements = Array.from(
            this.container.querySelectorAll(focusableSelectors)
        ).filter(el => {
            return el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement;
        });
    }

    handleTabKey(e) {
        if (!this.focusableElements || this.focusableElements.length === 0) return;

        const firstElement = this.focusableElements[0];
        const lastElement = this.focusableElements[this.focusableElements.length - 1];

        if (e.shiftKey) {
            // Shift + Tab
            if (document.activeElement === firstElement) {
                e.preventDefault();
                lastElement.focus();
            }
        } else {
            // Tab
            if (document.activeElement === lastElement) {
                e.preventDefault();
                firstElement.focus();
            }
        }
    }
}

// Automatic initialization upon loading
document.addEventListener('DOMContentLoaded', () => {
    const modals = document.querySelectorAll('.contact-us-modal');
    modals.forEach(modal => new ContactModal(modal));
});

// For Shopify sections (dynamic reloading)
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', (e) => {
        const modals = e.target.querySelectorAll('.contact-us-modal');
        modals.forEach(modal => new ContactModal(modal));
    });
}
